/* eslint-disable no-param-reassign */
import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getInstance as getBootstrap } from 'utils/bootstrap';
import { getOcbDomain } from 'utils/getDomains';
import ZoomIframeHTMLTemplate from 'common/compounds/VirtualEventRenderer/zoom_iframe_embed.ejs';

const zoomSdkKey = getBootstrap().get('zoomSdkKey');
const ZOOM_SDK_VERSION = getBootstrap().get('zoomSdkVersion');

const ZoomEmbed = ({ guest, meetingId, password, signature, features, onLeaveMeeting, iframeRef, obfToken }) => {
    const ZoomHTML = useMemo(
        () =>
            ZoomIframeHTMLTemplate({
                meetingId,
                signature,
                loggingEnabled: false,
                isLegacyLivestream: false,
                meetingConfig: JSON.stringify({
                    sdkKey: zoomSdkKey,
                    meetingNumber: meetingId,
                    userName: `${guest.firstName} ${guest.lastName}`,
                    userEmail: guest.email || '',
                    passWord: password,
                    role: 0,
                    chatEnabled: features.supportsChat,
                    showGuestEmailsEnabled: features.showGuestEmails,
                    ocbDomain: getOcbDomain(),
                }),
                zoomVersion: ZOOM_SDK_VERSION,
                obfToken,
            }),
        [meetingId, password, features.supportsChat, features.showGuestEmails, obfToken],
    );

    useEffect(() => {
        const isIFrameCrossOriginAccessible = iframeRef?.current?.contentDocument;

        if (isIFrameCrossOriginAccessible) {
            iframeRef.current.contentWindow.document.open();
            iframeRef.current.contentWindow.document.write(ZoomHTML);
            iframeRef.current.contentWindow.document.close();
        }
    }, [meetingId, ZoomHTML]);

    useEffect(() => {
        const isIFrameCrossOriginAccessible = iframeRef?.current?.contentDocument;

        if (isIFrameCrossOriginAccessible) {
            iframeRef.current.onload = () => {
                const { ZoomMtg } = iframeRef.current.contentWindow;
                ZoomMtg &&
                    ZoomMtg.inMeetingServiceListener('onMeetingStatus', (data) => {
                        const MEETING_STATUS_DISCONNECTED = 3;
                        if (data?.meetingStatus === MEETING_STATUS_DISCONNECTED) {
                            // This timeout is to prevent the zoom iframe from being destroyed before any error or
                            // notification has been read by our users.
                            setTimeout(() => {
                                onLeaveMeeting({ meetingId });
                            }, 4000);
                        }
                    });
            };

            iframeRef.current.contentWindow.onunload = (e) => {
                e.preventDefault();
                e.stopPropagation();

                if (iframeRef.current) {
                    // We detect if we are leaving the meeting by checking the inner window.ZoomMtg meetingObject. If the status is disconnected, we are leaving.
                    const { ZoomMtg } = iframeRef.current.contentWindow;
                    if (ZoomMtg && ZoomMtg.currentMeetingInfo.status === 'disconnect') {
                        // This timeout is to prevent the zoom iframe from being destroyed before any error or notification has been read by our users.
                        setTimeout(() => {
                            onLeaveMeeting({ meetingId });
                        }, 4000);
                    }
                }
            };
        }
    }, [meetingId, iframeRef.current]);

    // In order to avoid re-renders caused by the re-fetch of sessions done by react-query we use the useMemo hook listening ONLY to meetingID changes.
    const iframeCode = useMemo(
        () => (
            <iframe
                width="100%"
                height="100%"
                sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                allow="microphone; camera; fullscreen"
                ref={iframeRef}
                key={meetingId}
                title="Zoom Meeting"
            />
        ),
        [meetingId],
    );
    return <React.Fragment>{iframeCode}</React.Fragment>;
};

ZoomEmbed.propTypes = {
    meetingId: PropTypes.string.isRequired,
    onLeaveMeeting: PropTypes.func.isRequired,
    guest: PropTypes.object.isRequired,
    password: PropTypes.string.isRequired,
    signature: PropTypes.string.isRequired,
    features: PropTypes.object.isRequired,
    iframeRef: PropTypes.object.isRequired,
    obfToken: PropTypes.string.isRequired,
};

export default React.memo(ZoomEmbed);
